import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Fingerprint, KeyRound, Plus, Shield, Users, type LucideIcon } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Button, PageHeader, Tabs, TabsList, TabsTrigger } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { ApiClientsPanel } from "../features/access/ApiClientsPanel";
import { IdentityProvidersPanel } from "../features/access/IdentityProvidersPanel";
import { RolesMatrix } from "../features/access/RolesMatrix";
import { UsersDirectory } from "../features/access/UsersDirectory";

type Tab = "users" | "roles" | "api-clients" | "providers";

const TABS: Array<{ key: Tab; icon: LucideIcon; permission: string }> = [
  { key: "users", icon: Users, permission: "platform.users.read" },
  { key: "roles", icon: Shield, permission: "platform.roles.read" },
  { key: "api-clients", icon: KeyRound, permission: "platform.apiclients.read" },
  { key: "providers", icon: Fingerprint, permission: "platform.identity.read" },
];

const tabLabelKey: Record<Tab, string> = { users: "users", roles: "roles", "api-clients": "apiClients", providers: "providers" };

/** Identity & Access: directory, roles & permission matrix, API clients (client credentials) and staff identity providers. */
export function IdentityPage() {
  const { t } = useTranslation();
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const allowed = TABS.filter((x) => can(x.permission));
  const tab: Tab = (allowed.some((x) => x.key === tabParam) ? (tabParam as Tab) : allowed[0]?.key) ?? "users";
  const [creatingRole, setCreatingRole] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [creatingProvider, setCreatingProvider] = useState(false);

  useHelpTopic({ title: t("admin:identity.title"), docsAnchor: "identity", body: (<><p>{t("admin:identity.help.roles")}</p><p>{t("admin:identity.help.users")}</p><p>{t("admin:identity.help.apiClients")}</p><p>{t("admin:identity.help.providers")}</p></>) });
  useBreadcrumbs(useMemo(() => (tabParam ? [{ label: t(`admin:identity.tabs.${tabLabelKey[tab]}`) }] : null), [tab, tabParam, t]));
  usePaletteActions(useMemo(() => [
    ...(can("platform.roles.write") ? [{ id: "roles.new", label: t("admin:identity.roles.newRole"), icon: Shield, permission: "platform.roles.write", run: () => { navigate("/identity/roles"); setCreatingRole(true); } }] : []),
    ...(can("platform.apiclients.write") ? [{ id: "apiclients.new", label: t("admin:identity.apiClients.newClient"), icon: KeyRound, permission: "platform.apiclients.write", run: () => { navigate("/identity/api-clients"); setCreatingClient(true); } }] : []),
    ...(can("platform.identity.write") ? [{ id: "providers.new", label: t("admin:identity.providers.newProvider"), icon: Fingerprint, permission: "platform.identity.write", run: () => { navigate("/identity/providers"); setCreatingProvider(true); } }] : []),
  ], [can, navigate, t]));

  const action = tab === "roles" && can("platform.roles.write") ? <Button variant="gold" onClick={() => setCreatingRole(true)}><Plus /> {t("admin:identity.roles.newRole")}</Button>
    : tab === "api-clients" && can("platform.apiclients.write") ? <Button variant="gold" onClick={() => setCreatingClient(true)}><Plus /> {t("admin:identity.apiClients.newClient")}</Button>
    : tab === "providers" && can("platform.identity.write") ? <Button variant="gold" onClick={() => setCreatingProvider(true)}><Plus /> {t("admin:identity.providers.newProvider")}</Button>
    : null;

  return (
    <div>
      <PageHeader title={t("admin:identity.title")} description={t("admin:identity.subtitle")} actions={action} />
      <Tabs value={tab} onValueChange={(v) => navigate(v === "users" ? "/identity" : `/identity/${v}`)} className="mb-5">
        <TabsList variant="underline">
          {allowed.map((x) => <TabsTrigger key={x.key} value={x.key} variant="underline"><x.icon className="size-4" /> {t(`admin:identity.tabs.${tabLabelKey[x.key]}`)}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {tab === "users" ? <UsersDirectory /> : null}
      {tab === "roles" ? <RolesMatrix creating={creatingRole} onCreatingChange={setCreatingRole} /> : null}
      {tab === "api-clients" ? <ApiClientsPanel creating={creatingClient} onCreatingChange={setCreatingClient} /> : null}
      {tab === "providers" ? <IdentityProvidersPanel creating={creatingProvider} onCreatingChange={setCreatingProvider} /> : null}
    </div>
  );
}
